import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import ContextMenu
  from '@avtechsoftware/react-component-library/src/components/common/ContextMenu/ContextMenu';
import ContextMenuItem
  from '@avtechsoftware/react-component-library/src/components/common/ContextMenu/ContextMenuItem/ContextMenuItem';
import { useHistory } from 'react-router-dom';
import {
  createSavedSearch,
  deleteSavedSearch,
  selectSavedSearches,
  selectSavedSearchesCrudOperation,
  selectSavedSearchesCrudStatus,
} from '../../redux/slices/savedSearchesSlice/savedSearchesSlice';
import AdvancedSearchUtility from '../../utility/AdvancedSearchUtility';
import { connectModalContainerContextToComponent } from '../../contexts/ModalContext/ModalContext';
import SavedSearchDetailsModal from './SavedSearchDetailsModal';
import DeleteConfirmationModal from '../common/DeleteConfirmationModal';
import EditSavedSearchModal from './EditSavedSearchModal';
import { CrudType } from '../../utility/types/CrudType';
import { CrudStatus } from '../../utility/types/CrudStatus';
import SortableTableHeader from '../common/SortableTableHeader/SortableTableHeader';
import SearchType from '../../utility/types/SearchType';
import { RaaRoles } from '../../utility/SecurityRules';
import HasRolePrivileges from '../highorder/HasRolePrivileges';
import { setAppliedAlertSearch } from '../../redux/slices/alertListSlice/alertListSlice';
import {
  setAppliedDeviceSearch,
} from '../../redux/slices/filteredDeviceListSlice/filteredDeviceListSlice';

/* istanbul ignore next */
function SavedSearchesTable({ setModalJSX, searchType }) {
  const savedSearched = useSelector(selectSavedSearches);
  const dispatch = useDispatch();
  const history = useHistory();
  const crudOperation = useSelector(selectSavedSearchesCrudOperation);
  const crudStatus = useSelector(selectSavedSearchesCrudStatus);
  const entityName = searchType === SearchType.ALERT ? 'Alert' : 'Device';

  const deleteInProgress = crudOperation === CrudType.DELETE
    && crudStatus === CrudStatus.PENDING;

  const [sortCol, setSortCol] = useState('name');
  const [sortOrder, setSortOrder] = useState('ASC');
  const [sortedList, setSortedList] = useState(savedSearched);

  const handleSetSortingOrder = (colName, order) => {
    setSortCol(colName);
    setSortOrder(order);
  };

  const handleViewInAlertsList = (savedSearch) => {
    setModalJSX((<></>));
    dispatch(setAppliedAlertSearch(savedSearch));
    history.push('/s/alerts');
  };

  const handleViewInDevicesList = (savedSearch) => {
    setModalJSX((<></>));
    dispatch(setAppliedDeviceSearch(savedSearch));
    history.push('/s/devices');
  };

  const handleViewInEntityList = (savedSearch) => (searchType === SearchType.ALERT
    ? handleViewInAlertsList(savedSearch)
    : handleViewInDevicesList(savedSearch));

  useEffect(() => {
    const sorted = savedSearched.map((item) => ({
      ...item,
      filterCount: Object.keys(AdvancedSearchUtility.sortFiltersByFacetType(item.filters)).length,
    }))
      .sort((a, b) => {
        const itemA = String(a[sortCol])
          .toLowerCase();
        const itemB = String(b[sortCol])
          .toLowerCase();

        if (sortOrder === 'ASC') {
          if (itemA < itemB) return -1;
          if (itemA > itemB) return 1;
        } else {
          if (itemA > itemB) return -1;
          if (itemA < itemB) return 1;
        }
        return 0;
      });
    setSortedList(sorted);
  }, [sortCol, sortOrder, savedSearched]);

  return sortedList.length > 0 ? (
    <table className="table table-sm table-striped">
      <thead>
        <tr>
          <SortableTableHeader
            onAscClick={
              () => handleSetSortingOrder('name', 'ASC')
            }
            onDescClick={() => handleSetSortingOrder('name', 'DES')}
            label="Search Name"
          />
          <SortableTableHeader
            onAscClick={
              () => handleSetSortingOrder('description', 'ASC')
            }
            onDescClick={() => handleSetSortingOrder('description', 'DES')}
            label="Description"
          />
          <SortableTableHeader
            onAscClick={
              () => handleSetSortingOrder('filterCount', 'ASC')
            }
            onDescClick={() => handleSetSortingOrder('filterCount', 'DES')}
            label="Filters"
          />
          {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
          <th className="text-right" />
        </tr>
      </thead>
      <tbody>
        {sortedList.map((item, index) => {
          const sortedFilters = AdvancedSearchUtility
            .sortFiltersByFacetType(item.filters);
          return (
            <tr
              key={index}
              style={{ cursor: 'pointer' }}
              onClick={() => setModalJSX(
                <SavedSearchDetailsModal
                  searchType={searchType}
                  searchId={item.id}
                  handleEditClick={
                  () => setModalJSX(<EditSavedSearchModal
                    savedSearchId={item.id}
                    handleViewInListClick={() => handleViewInEntityList(item)}
                  />)
                  }
                  handleViewInListClick={() => handleViewInEntityList(item)}
                />,
              )}
            >
              <td className="text-left">
                {item.name}
              </td>
              <td className="text-left">
                {item.description}
              </td>
              <td className="text-left">
                {Object.keys(sortedFilters).length}
              </td>
              <td className="text-left">
                <ContextMenu classname="overflow-visible">
                  <>
                    <HasRolePrivileges targetRoleList={[RaaRoles.ADMIN, RaaRoles.MANAGER]}>
                      <>
                        <ContextMenuItem
                          itemIcon="pencil"
                          itemText="Edit"
                          onClickHandler={(event) => {
                            event.stopPropagation();
                            setModalJSX(
                              <EditSavedSearchModal
                                savedSearchId={item.id}
                                handleViewInListClick={() => handleViewInEntityList(item)}
                                searchType={searchType}
                              />,
                            );
                          }}
                        />
                        <ContextMenuItem
                          itemIcon="copy"
                          itemText="Clone"
                          onClickHandler={(event) => {
                            event.stopPropagation();
                            dispatch(createSavedSearch({
                              ...item,
                              name: `CLONE - ${item.name}`,
                            }));
                          }}
                        />
                        <ContextMenuItem
                          itemIcon="trash"
                          itemText="Delete"
                          onClickHandler={(event) => {
                            event.stopPropagation();
                            setModalJSX(
                              <DeleteConfirmationModal
                                itemName={item.name}
                                handleDelete={() => {
                                  dispatch(deleteSavedSearch(item.id));
                                  setModalJSX(<></>);
                                }}
                                isButtonDisabled={deleteInProgress}
                              />,
                            );
                          }}
                        />
                      </>
                    </HasRolePrivileges>
                    <ContextMenuItem
                      itemIcon="info-circle"
                      itemText="View"
                      onClickHandler={(event) => {
                        event.stopPropagation();
                        setModalJSX(
                          <SavedSearchDetailsModal
                            searchType={searchType}
                            searchId={item.id}
                            handleEditClick={
                            () => setModalJSX(<EditSavedSearchModal
                              savedSearchId={item.id}
                              handleViewInListClick={() => handleViewInEntityList(item)}
                            />)
                          }
                            handleViewInListClick={() => handleViewInEntityList(item)}
                          />,
                        );
                      }}
                    />
                  </>
                </ContextMenu>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  )
    : (
      <div className="row">
        <div className="col">
          <ul>
            <li>
              A saved search is a collection of filters that can be applied to your
              {' '}
              {searchType === SearchType.ALERT ? 'Alerts' : 'Devices'}
              {' '}
              List. By
              filtering on criteria such as
              {' '}
              {entityName.toLowerCase()}
              {' '}
              name, tags, and more, you can view a
              particular subset of your
              {' '}
              {entityName.toLowerCase()}
              s... or narrow-down the search to help you find that
              one
              {' '}
              {entityName.toLowerCase()}
              {' '}
              you&apos;re looking for.
            </li>
            <li>
              Save your search with a unique name and optional description for quick and convenient
              access later on.
            </li>
            <li>
              To get started, go to your
              {' '}
              <a href={searchType === SearchType.ALERT ? '/s/alerts' : '/s/devices'}>
                {entityName}
                s List
              </a>
              {' '}
              and click into the Search bar to select a
              filter.
            </li>
            <li>
              Learn more:
              {' '}
              <a
                href="https://avtech.com/articles/?p=27259"
                target="_blank"
                rel="noopener noreferrer"
              >
                {' '}
                How To Save Searches In Room Alert Account
              </a>
            </li>
          </ul>
        </div>
      </div>
    );
}

SavedSearchesTable.propTypes = {
  setModalJSX: PropTypes.func.isRequired,
  searchType: PropTypes.oneOf([0, 1, 2]).isRequired,
};

export default connectModalContainerContextToComponent(SavedSearchesTable);
